
import React, { useMemo, useState } from 'react';
import ReactDOM from 'react-dom';
import { MapContainer, TileLayer, Polygon as LeafletPolygon, useMapEvents, Marker, Popup } from 'react-leaflet';
import { FaBuilding, FaChevronLeft, FaChevronRight, FaDrawPolygon, FaEdit, FaFilter, FaGasPump, FaListUl, FaMapMarkerAlt, FaPlus, FaSearch, FaShieldAlt, FaTimes, FaTrash, FaWrench } from 'react-icons/fa';
import L from 'leaflet';
import axios from 'axios';

// Componente auxiliar para centralizar o mapa via ref
function MapaCentralizador({ center }: { center: [number, number] }) {
  const map = useMapEvents({});
  React.useEffect(() => {
    if (center) map.setView(center, map.getZoom());
  }, [center]);
  return null;
}

interface Poligono {
  id?: number;
  nome: string;
  tipo: 'patio' | 'cliente' | 'posto' | 'oficina' | 'outro';
  cnpjCliente?: string;
  descricao?: string;
  coordenadas: Array<{ lat: number; lng: number }>;
  cor: string;
  ativa?: boolean;
}

const formatarCnpj = (valor: string) => {
  const numeros = String(valor || '').replace(/\D/g, '').slice(0, 14);
  return numeros
    .replace(/^(\d{2})(\d)/, '$1.$2')
    .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
    .replace(/\.(\d{3})(\d)/, '.$1/$2')
    .replace(/(\d{4})(\d)/, '$1-$2');
};

interface PoligonoEditorProps {
  poligonos: Poligono[];
  onSalvar: (poligono: Poligono) => void;
  onAtualizar: (id: number, poligono: Poligono) => void;
  onDeletar: (id: number) => void;
  showListaPoligonos: boolean;
  setShowListaPoligonos: (show: boolean) => void;
}

// Componente para capturar cliques no mapa
function PoligonoDrawer({ pontos, onAddPonto, onRemoverPonto, onMoverPonto }: { 
  pontos: Array<{ lat: number; lng: number }>, 
  onAddPonto: (lat: number, lng: number) => void, 
  onRemoverPonto: (index: number) => void,
  onMoverPonto: (index: number, lat: number, lng: number) => void
}) {
  useMapEvents({
    click(e) {
      onAddPonto(e.latlng.lat, e.latlng.lng);
    },
  });
  
  // Criar ícone para os pontos
  return (
    <>
      {/* Desenhar marcadores nos pontos (arrastáveis) */}
      {pontos.map((ponto, index) => (
        <Marker 
          key={index} 
          position={[ponto.lat, ponto.lng]}
          icon={L.divIcon({
            className: 'polygon-number-marker',
            html: `<span>${index + 1}</span>`,
            iconSize: [30, 30],
            iconAnchor: [15, 15]
          })}
          draggable={true}
          eventHandlers={{
            click: (e) => {
              L.DomEvent.stopPropagation(e);
            },
            dragend: (e) => {
              const marker = e.target;
              const position = marker.getLatLng();
              onMoverPonto(index, position.lat, position.lng);
            }
          }}
        >
          <Popup>
            <div style={{ textAlign: 'center' }}>
              <strong>Ponto {index + 1}</strong>
              <br />
              <small>{ponto.lat.toFixed(6)}, {ponto.lng.toFixed(6)}</small>
              <br />
              <small style={{ color: '#4caf50', fontSize: 10, fontWeight: 'bold' }}>
                ✋ Arraste para mover
              </small>
              <br />
              <small style={{ color: '#666', fontSize: 10 }}>
                Use o painel lateral para remover
              </small>
            </div>
          </Popup>
        </Marker>
      ))}
      
      {/* Desenhar polígono temporário se houver 3+ pontos */}
      {pontos.length >= 3 && (
        <LeafletPolygon 
          positions={pontos.map(p => [p.lat, p.lng])} 
          pathOptions={{ 
            color: '#ff7a00', 
            fillColor: '#ff9800',
            fillOpacity: 0.2,
            weight: 3,
            dashArray: '10, 10' // Linha tracejada para indicar que está em edição
          }} 
        />
      )}
      
      {/* Desenhar linhas conectando os pontos se houver 2+ pontos */}
      {pontos.length >= 2 && pontos.length < 3 && (
        <LeafletPolygon 
          positions={pontos.map(p => [p.lat, p.lng])} 
          pathOptions={{ 
            color: '#ff7a00', 
            fillOpacity: 0,
            weight: 2,
            dashArray: '5, 5'
          }} 
        />
      )}
    </>
  );
}

const PoligonoEditor: React.FC<PoligonoEditorProps> = ({ poligonos, onSalvar, onAtualizar, onDeletar, showListaPoligonos, setShowListaPoligonos }) => {
  const [showModal, setShowModal] = useState(false);
  const [modoDesenho, setModoDesenho] = useState(false);
  const [poligonoEmEdicao, setPoligonoEmEdicao] = useState<number | null>(null);
  const [painelLateralAberto, setPainelLateralAberto] = useState(true);
  const [novoPoligono, setNovoPoligono] = useState<Poligono>({
    nome: '',
    tipo: 'outro',
    cnpjCliente: '',
    descricao: '',
    coordenadas: [],
    cor: '#2196f3'
  });

  // Busca de endereço e lat/lng
  const [buscaEndereco, setBuscaEndereco] = useState('');
  const [buscandoEndereco, setBuscandoEndereco] = useState(false);
  const [erroEndereco, setErroEndereco] = useState('');
  const [mapCenter, setMapCenter] = useState<[number, number]>([-23.5505, -46.6333]); // São Paulo padrão
  const [mapZoom, setMapZoom] = useState(13);
  const [marcadorBusca, setMarcadorBusca] = useState<[number, number] | null>(null);
  const [latInput, setLatInput] = useState('');
  const [lngInput, setLngInput] = useState('');
  const [buscaPoligono, setBuscaPoligono] = useState('');
  const [filtroTipoPoligono, setFiltroTipoPoligono] = useState<'todos' | Poligono['tipo']>('todos');
  const [paginaPoligonos, setPaginaPoligonos] = useState(1);
  const [itensPorPaginaPoligonos, setItensPorPaginaPoligonos] = useState(5);

  // Buscar endereço e centralizar o mapa
  const buscarEndereco = async () => {
    if (!buscaEndereco.trim()) return;
    setBuscandoEndereco(true);
    setErroEndereco('');
    try {
      const resp = await axios.get('/api/monitoramento/geocode-search', {
        params: { q: buscaEndereco }
      });
      const data = resp.data?.results;
      if (data && data.length > 0) {
        const { lat, lon } = data[0];
        const latNum = parseFloat(lat);
        const lonNum = parseFloat(lon);
        setMapCenter([latNum, lonNum]);
        setMapZoom(17);
        setLatInput(lat);
        setLngInput(lon);
        setMarcadorBusca([latNum, lonNum]);
      } else {
        setErroEndereco('Endereço não encontrado.');
      }
    } catch (err) {
      setErroEndereco('Erro ao buscar endereço.');
    } finally {
      setBuscandoEndereco(false);
    }
  };

  // Buscar por latitude/longitude e preencher endereço
  const buscarPorLatLng = async () => {
    if (!latInput.trim() || !lngInput.trim()) return;
    setBuscandoEndereco(true);
    setErroEndereco('');
    try {
      const resp = await axios.get('/api/monitoramento/reverse-geocode', {
        params: { lat: latInput, lng: lngInput }
      });
      const data = { display_name: resp.data?.display_name || resp.data?.endereco || '' };
      const latNum = parseFloat(latInput);
      const lonNum = parseFloat(lngInput);
      if (data && data.display_name) {
        setBuscaEndereco(data.display_name);
        setMapCenter([latNum, lonNum]);
        setMapZoom(17);
        setMarcadorBusca([latNum, lonNum]);
        setNovoPoligono(prev => ({
          ...prev,
          coordenadas: [...prev.coordenadas, { lat: latNum, lng: lonNum }]
        }));
      } else {
        setErroEndereco('Endereço não encontrado para as coordenadas.');
      }
    } catch (err) {
      setErroEndereco('Erro ao buscar endereço pelas coordenadas.');
    } finally {
      setBuscandoEndereco(false);
    }
  };

  const handleAddPonto = (lat: number, lng: number) => {
    setNovoPoligono(prev => ({
      ...prev,
      coordenadas: [...prev.coordenadas, { lat, lng }]
    }));
  };

  const handleRemoverPonto = (index: number) => {
    setNovoPoligono(prev => ({
      ...prev,
      coordenadas: prev.coordenadas.filter((_, i) => i !== index)
    }));
  };

  const handleMoverPonto = (index: number, lat: number, lng: number) => {
    setNovoPoligono(prev => ({
      ...prev,
      coordenadas: prev.coordenadas.map((ponto, i) => 
        i === index ? { lat, lng } : ponto
      )
    }));
  };

  const handleFinalizarDesenho = () => {
    if (novoPoligono.coordenadas.length < 3) {
      alert('Um polígono precisa ter no mínimo 3 pontos');
      return;
    }
    setModoDesenho(false);
    setShowModal(true);
  };

  const handleSalvar = () => {
    if (!novoPoligono.nome) {
      alert('Nome é obrigatório');
      return;
    }
    
    if (novoPoligono.coordenadas.length < 3) {
      alert('Um polígono precisa ter no mínimo 3 pontos');
      return;
    }

    const cnpjNumeros = String(novoPoligono.cnpjCliente || '').replace(/\D/g, '');
    if (cnpjNumeros && cnpjNumeros.length !== 14) {
      alert('O CNPJ do cliente deve possuir 14 números');
      return;
    }

    if (poligonoEmEdicao) {
      onAtualizar(poligonoEmEdicao, novoPoligono);
    } else {
      onSalvar(novoPoligono);
    }

    // Resetar
    setNovoPoligono({
      nome: '',
      tipo: 'outro',
      cnpjCliente: '',
      descricao: '',
      coordenadas: [],
      cor: '#2196f3'
    });
    setShowModal(false);
    setPoligonoEmEdicao(null);
  };

  const handleEditar = (poligono: Poligono) => {
    setNovoPoligono(poligono);
    setPoligonoEmEdicao(poligono.id || null);
    setModoDesenho(true); // Ativar modo desenho para permitir editar pontos
    setShowListaPoligonos(false);
  };

  const handleCancelar = () => {
    setNovoPoligono({
      nome: '',
      tipo: 'outro',
      cnpjCliente: '',
      descricao: '',
      coordenadas: [],
      cor: '#2196f3'
    });
    setShowModal(false);
    setModoDesenho(false);
    setPoligonoEmEdicao(null);
  };

  const handleNovoPoligono = () => {
    setNovoPoligono({
      nome: '',
      tipo: 'outro',
      cnpjCliente: '',
      descricao: '',
      coordenadas: [],
      cor: '#2196f3'
    });
    setPoligonoEmEdicao(null);
    setModoDesenho(true);
    setShowListaPoligonos(false);
  };

  const getTipoLabel = (tipo: string) => {
    const tipos: { [key: string]: string } = {
      'patio': 'Pátio',
      'cliente': 'Cliente',
      'posto': 'Posto',
      'oficina': 'Oficina',
      'outro': 'Outro'
    };
    return tipos[tipo] || tipo;
  };

  const getTipoCor = (tipo: string) => {
    const cores: { [key: string]: string } = {
      'patio': '#4caf50',
      'cliente': '#2196f3',
      'posto': '#ff9800',
      'oficina': '#f44336',
      'outro': '#9e9e9e'
    };
    return cores[tipo] || '#9e9e9e';
  };

  const getTipoIcon = (tipo: Poligono['tipo']) => {
    const icons: Record<Poligono['tipo'], React.ReactNode> = {
      patio: <FaDrawPolygon />,
      cliente: <FaBuilding />,
      posto: <FaGasPump />,
      oficina: <FaWrench />,
      outro: <FaMapMarkerAlt />
    };
    return icons[tipo] || icons.outro;
  };

  const poligonosFiltrados = useMemo(() => {
    const termo = buscaPoligono.trim().toLowerCase();

    return poligonos.filter(poligono => {
      const combinaBusca = !termo
        || poligono.nome.toLowerCase().includes(termo)
        || (poligono.cnpjCliente || '').includes(termo.replace(/\D/g, ''))
        || (poligono.descricao || '').toLowerCase().includes(termo);
      const combinaTipo = filtroTipoPoligono === 'todos' || poligono.tipo === filtroTipoPoligono;

      return combinaBusca && combinaTipo;
    });
  }, [buscaPoligono, filtroTipoPoligono, poligonos]);

  const totalPaginasPoligonos = Math.max(1, Math.ceil(poligonosFiltrados.length / itensPorPaginaPoligonos));
  const paginaAtualPoligonos = Math.min(paginaPoligonos, totalPaginasPoligonos);
  const inicioPoligonos = (paginaAtualPoligonos - 1) * itensPorPaginaPoligonos;
  const poligonosPaginados = poligonosFiltrados.slice(inicioPoligonos, inicioPoligonos + itensPorPaginaPoligonos);
  const primeiroItemPoligonos = poligonosFiltrados.length === 0 ? 0 : inicioPoligonos + 1;
  const ultimoItemPoligonos = Math.min(inicioPoligonos + itensPorPaginaPoligonos, poligonosFiltrados.length);
  const paginasVisiveisPoligonos = useMemo(() => {
    const maxPaginasVisiveis = 5;
    const inicio = Math.max(
      1,
      Math.min(
        paginaAtualPoligonos - Math.floor(maxPaginasVisiveis / 2),
        Math.max(1, totalPaginasPoligonos - maxPaginasVisiveis + 1)
      )
    );
    const fim = Math.min(totalPaginasPoligonos, inicio + maxPaginasVisiveis - 1);

    return Array.from({ length: fim - inicio + 1 }, (_, index) => inicio + index);
  }, [paginaAtualPoligonos, totalPaginasPoligonos]);

  const mudarPaginaPoligonos = (pagina: number) => {
    setPaginaPoligonos(Math.min(Math.max(pagina, 1), totalPaginasPoligonos));
  };

  return (
    <>
      {/* Botão oculto controlado pelo header */}
      <button
        data-poligono-button
        onClick={() => setShowListaPoligonos(!showListaPoligonos)}
        style={{ display: 'none' }}
      />

      {/* Modal de desenho no mapa */}
      {modoDesenho && ReactDOM.createPortal(
        <div className="polygon-draw-overlay" style={{
          position: 'fixed', 
          zIndex: 2147483647, 
          left: 0, 
          top: 0, 
          width: '100vw', 
          height: '100vh', 
          background: 'rgba(0,0,0,0.5)',
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center',
          padding: '10px'
        }}>
          <div className="modal-overlay" onClick={(e) => {
            if (e.target === e.currentTarget) {
              // Não fecha ao clicar no fundo - precisa usar os botões
            }
          }} style={{position: 'static', zIndex: 'auto', width: '100%', height: '100%'}}>
            <div className="modal-content polygon-draw-modal" onClick={(e) => e.stopPropagation()} style={{ 
              width: '100%', 
              height: '100%',
              maxWidth: '100%',
              maxHeight: '100%',
              margin: 0
            }}>
              <div className="modal-header polygon-draw-header" style={{ 
                background: poligonoEmEdicao ? 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)' : 'linear-gradient(135deg, #2196f3 0%, #1976d2 100%)',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <div className="polygon-draw-heading">
                  <span className="polygon-draw-heading-icon"><FaEdit /></span>
                  <div>
                    <h3>{poligonoEmEdicao ? 'Editar Polígono' : 'Novo Polígono'}</h3>
                    <p>Adicione, arraste ou remova pontos no mapa para ajustar a área.</p>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                  <button 
                    onClick={() => setPainelLateralAberto(!painelLateralAberto)}
                    style={{
                      background: 'rgba(255,255,255,0.2)',
                      border: '1px solid rgba(255,255,255,0.4)',
                      color: 'white',
                      padding: '6px 12px',
                      borderRadius: 4,
                      cursor: 'pointer',
                      fontSize: 12,
                      fontWeight: 600,
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.3)'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.2)'}
                  >
                    {painelLateralAberto ? 'Ocultar painel' : 'Mostrar painel'}
                  </button>
                  <button className="modal-close" onClick={handleCancelar}>×</button>
                </div>
              </div>
              <div className="modal-body polygon-draw-body" style={{ padding: 0, display: 'flex', gap: 0, height: 'calc(100% - 60px)' }}>
                {/* Mapa à esquerda */}
                <div className="polygon-draw-map" style={{ flex: 1, position: 'relative', height: '100%' }}>
                  {novoPoligono.coordenadas.length > 0 && (
                    <button
                      className="polygon-clear-button"
                      type="button"
                      onClick={() => setNovoPoligono(prev => ({ ...prev, coordenadas: [] }))}
                    >
                      <FaTrash /> Limpar polígono
                    </button>
                  )}
                  <MapContainer
                    center={mapCenter}
                    zoom={mapZoom}
                    style={{ height: '100%', width: '100%' }}
                  >
                    <MapaCentralizador center={mapCenter} />
                    <TileLayer
                      attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                    {marcadorBusca && (
                      <Marker position={marcadorBusca} icon={L.icon({
                        iconUrl: 'https://cdn-icons-png.flaticon.com/512/684/684908.png',
                        iconSize: [32, 32],
                        iconAnchor: [16, 32],
                        popupAnchor: [0, -32],
                      })}>
                        <Popup>Local buscado</Popup>
                      </Marker>
                    )}
                    <PoligonoDrawer 
                      pontos={novoPoligono.coordenadas} 
                      onAddPonto={handleAddPonto} 
                      onRemoverPonto={handleRemoverPonto}
                      onMoverPonto={handleMoverPonto}
                    />
                  </MapContainer>
                </div>
                {/* Painel lateral direito - colapsável */}
                {painelLateralAberto && (
                  <div className="polygon-draw-panel" style={{ 
                    minWidth: 300, 
                    width: 300, 
                    maxWidth: 300, 
                    background: 'white', 
                    padding: 16, 
                    overflowY: 'auto', 
                    borderLeft: '2px solid #e0e0e0', 
                    boxSizing: 'border-box', 
                    display: 'flex', 
                    flexDirection: 'column', 
                    gap: 14,
                    transition: 'all 0.3s ease'
                  }}>
                  {/* CAMPO DE BUSCA DE ENDEREÇO E LAT/LNG */}
                  <div className="polygon-draw-card polygon-location-card" style={{ background: 'linear-gradient(135deg, #f5f5f5 0%, #eeeeee 100%)', padding: 14, borderRadius: 8, border: '1px solid #ddd' }}>
                    <div style={{ fontWeight: 600, marginBottom: 10, color: '#424242', fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
                      🔍 <span>Localizar no Mapa</span>
                    </div>
                    
                    {/* Busca por endereço */}
                    <div style={{ marginBottom: 12 }}>
                      <label style={{ fontSize: 11, color: '#666', display: 'block', marginBottom: 4, fontWeight: 500 }}>Por endereço:</label>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <input
                          type="text"
                          value={buscaEndereco}
                          onChange={e => setBuscaEndereco(e.target.value)}
                          placeholder="Ex: Rua ABC, 123"
                          style={{ flex: 1, padding: '8px 10px', borderRadius: 4, border: '1px solid #ccc', fontSize: 13, background: 'white' }}
                          onKeyDown={e => { if (e.key === 'Enter') buscarEndereco(); }}
                          disabled={buscandoEndereco}
                        />
                        <button
                          onClick={buscarEndereco}
                          disabled={buscandoEndereco || !buscaEndereco.trim()}
                          style={{ padding: '8px 14px', borderRadius: 4, background: '#1976d2', color: 'white', border: 'none', fontWeight: 600, cursor: 'pointer', fontSize: 12, whiteSpace: 'nowrap', transition: 'all 0.2s' }}
                          onMouseEnter={e => { if (!e.currentTarget.disabled) e.currentTarget.style.background = '#1565c0'; }}
                          onMouseLeave={e => { if (!e.currentTarget.disabled) e.currentTarget.style.background = '#1976d2'; }}
                        >
                          {buscandoEndereco ? '...' : 'Ir'}
                        </button>
                      </div>
                    </div>
                    
                    {/* Busca por coordenadas */}
                    <div>
                      <label style={{ fontSize: 11, color: '#666', display: 'block', marginBottom: 4, fontWeight: 500 }}>Por coordenadas:</label>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <input
                          type="number"
                          value={latInput}
                          onChange={e => setLatInput(e.target.value)}
                          placeholder="Latitude"
                          style={{ flex: 1, padding: '8px 10px', borderRadius: 4, border: '1px solid #ccc', fontSize: 12, background: 'white' }}
                          step="any"
                          disabled={buscandoEndereco}
                        />
                        <input
                          type="number"
                          value={lngInput}
                          onChange={e => setLngInput(e.target.value)}
                          placeholder="Longitude"
                          style={{ flex: 1, padding: '8px 10px', borderRadius: 4, border: '1px solid #ccc', fontSize: 12, background: 'white' }}
                          step="any"
                          disabled={buscandoEndereco}
                        />
                        <button
                          onClick={buscarPorLatLng}
                          disabled={buscandoEndereco || !latInput.trim() || !lngInput.trim()}
                          style={{ padding: '8px 14px', borderRadius: 4, background: '#2e7d32', color: 'white', border: 'none', fontWeight: 600, cursor: 'pointer', fontSize: 12, whiteSpace: 'nowrap', transition: 'all 0.2s' }}
                          onMouseEnter={e => { if (!e.currentTarget.disabled) e.currentTarget.style.background = '#1b5e20'; }}
                          onMouseLeave={e => { if (!e.currentTarget.disabled) e.currentTarget.style.background = '#2e7d32'; }}
                        >
                          Adicionar
                        </button>
                      </div>
                      {erroEndereco && <div style={{ color: '#f44336', fontSize: 11, marginTop: 6, padding: 6, background: '#ffebee', borderRadius: 4 }}>{erroEndereco}</div>}
                    </div>
                  </div>
                  <div style={{ 
                    marginBottom: 15, 
                    padding: 12, 
                    background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)',
                    borderRadius: 8,
                    border: '1px solid #2196f3'
                  }}>
                    <div style={{ fontWeight: 600, fontSize: 13, color: '#1976d2', marginBottom: 8 }}>📍 Como editar:</div>
                    <div style={{ fontSize: 12, color: '#424242', lineHeight: 1.8 }}>
                      <div style={{ marginBottom: 4 }}>🖱️ <strong>Clique no mapa</strong> para adicionar pontos</div>
                      <div style={{ marginBottom: 4 }}>✋ <strong>Arraste marcadores</strong> para reposicionar</div>
                      <div>🗑️ <strong>Clique no × </strong> para remover</div>
                    </div>
                  </div>
                  <div style={{ 
                    marginBottom: 15, 
                    padding: 14, 
                    background: novoPoligono.coordenadas.length >= 3 
                      ? 'linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)' 
                      : 'linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%)',
                    borderRadius: 8,
                    border: `2px solid ${novoPoligono.coordenadas.length >= 3 ? '#4caf50' : '#ff9800'}`,
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <div style={{ fontSize: 12, color: '#666', marginBottom: 2 }}>Total de pontos</div>
                        <div style={{ fontSize: 24, fontWeight: 700, color: novoPoligono.coordenadas.length >= 3 ? '#2e7d32' : '#f57c00' }}>
                          {novoPoligono.coordenadas.length}
                        </div>
                      </div>
                      {novoPoligono.coordenadas.length >= 3 ? (
                        <div style={{ textAlign: 'right' }}>
                          <div style={{ fontSize: 28 }}>✓</div>
                          <div style={{ color: '#4caf50', fontSize: 11, fontWeight: 600 }}>Pronto!</div>
                        </div>
                      ) : (
                        <div style={{ textAlign: 'right' }}>
                          <div style={{ fontSize: 11, color: '#666' }}>Mínimo necessário</div>
                          <div style={{ color: '#ff9800', fontSize: 18, fontWeight: 700 }}>
                            3 pontos
                          </div>
                          <div style={{ fontSize: 10, color: '#999', marginTop: 2 }}>
                            Faltam {3 - novoPoligono.coordenadas.length}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  {novoPoligono.coordenadas.length > 0 && (
                    <div style={{ marginBottom: 15 }}>
                      <label style={{ display: 'block', marginBottom: 8, fontWeight: 600, fontSize: 13, color: '#424242' }}>📌 Pontos do Polígono:</label>
                      <div style={{ 
                        maxHeight: 280, 
                        overflowY: 'auto', 
                        background: '#fafafa', 
                        padding: 8, 
                        borderRadius: 6, 
                        border: '1px solid #e0e0e0'
                      }}>
                        {novoPoligono.coordenadas.map((p, i) => (
                          <div key={i} style={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: 'center', 
                            fontSize: 11,
                            marginBottom: 4,
                            padding: '8px 10px',
                            background: 'white',
                            borderRadius: 4,
                            border: '1px solid #e0e0e0',
                            transition: 'all 0.2s'
                          }}>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontWeight: 600, color: '#1976d2', marginBottom: 2, fontSize: 12 }}>Ponto {i + 1}</div>
                              <div style={{ color: '#666', fontFamily: 'monospace', fontSize: 10 }}>
                                Lat: {p.lat.toFixed(6)} | Lng: {p.lng.toFixed(6)}
                              </div>
                            </div>
                            <button
                              onClick={() => handleRemoverPonto(i)}
                              title="Remover ponto"
                              style={{ 
                                background: 'transparent',
                                color: '#f44336', 
                                border: '1px solid #f44336', 
                                padding: '4px 8px', 
                                borderRadius: 3, 
                                cursor: 'pointer', 
                                fontSize: 14,
                                fontWeight: 'bold',
                                transition: 'all 0.2s',
                                marginLeft: 8
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.background = '#f44336';
                                e.currentTarget.style.color = 'white';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.background = 'transparent';
                                e.currentTarget.style.color = '#f44336';
                              }}
                            >
                              <FaTrash />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  </div>
                )}
              </div>
              <div className="modal-footer polygon-draw-footer">
                <button className="btn-secondary" onClick={handleCancelar}>
                  × Cancelar
                </button>
                <button 
                  className="btn-primary" 
                  onClick={handleFinalizarDesenho}
                  disabled={novoPoligono.coordenadas.length < 3}
                  style={{
                    opacity: novoPoligono.coordenadas.length >= 3 ? 1 : 0.5,
                    cursor: novoPoligono.coordenadas.length >= 3 ? 'pointer' : 'not-allowed'
                  }}
                >
                  ✓ Continuar ({novoPoligono.coordenadas.length}/3 mínimo)
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Lista de Polígonos - Modal via Portal para cobrir toda a viewport */}
      {showListaPoligonos && ReactDOM.createPortal(
        <div className="polygon-manager-overlay" onClick={() => setShowListaPoligonos(false)}>
          <div className="polygon-manager-modal" onClick={(e) => e.stopPropagation()}>
            <aside className="polygon-manager-side">
              <div className="polygon-hero-art" aria-hidden="true">
                <svg viewBox="0 0 260 190" role="img">
                  <defs>
                    <linearGradient id="polygonArtFill" x1="0" y1="0" x2="1" y2="1">
                      <stop offset="0%" stopColor="#1d7df2" stopOpacity="0.92" />
                      <stop offset="100%" stopColor="#0f58db" stopOpacity="0.78" />
                    </linearGradient>
                    <filter id="polygonArtShadow" x="-20%" y="-20%" width="140%" height="140%">
                      <feDropShadow dx="0" dy="14" stdDeviation="12" floodColor="#2563eb" floodOpacity="0.22" />
                    </filter>
                  </defs>
                  <path d="M22 138 L70 56 L132 40 L220 72 L242 148 L146 130 L92 153 Z" fill="url(#polygonArtFill)" filter="url(#polygonArtShadow)" />
                  <path d="M22 138 L70 56 L132 40 L220 72 L242 148 L146 130 L92 153 Z" fill="none" stroke="#74b4ff" strokeWidth="5" />
                  <path d="M70 56 L92 153 M132 40 L146 130 M220 72 L146 130 M70 56 L146 130" stroke="#c9e4ff" strokeWidth="2" strokeDasharray="6 7" opacity="0.85" />
                  {[70, 132, 220, 146, 92].map((cx, index) => (
                    <circle key={index} cx={cx} cy={[56, 40, 72, 130, 153][index]} r="5" fill="#ffffff" opacity="0.95" />
                  ))}
                  <g fill="#ffffff" stroke="#287bff" strokeWidth="5">
                    <path d="M56 80c0-13 10-23 23-23s23 10 23 23c0 17-23 43-23 43S56 97 56 80z" />
                    <circle cx="79" cy="80" r="7" fill="#287bff" stroke="none" />
                    <path d="M187 64c0-12 10-22 22-22s22 10 22 22c0 16-22 42-22 42S187 80 187 64z" />
                    <circle cx="209" cy="64" r="7" fill="#287bff" stroke="none" />
                    <path d="M122 102c0-12 10-22 22-22s22 10 22 22c0 16-22 42-22 42S122 118 122 102z" />
                    <circle cx="144" cy="102" r="7" fill="#287bff" stroke="none" />
                  </g>
                </svg>
              </div>

              <div className="polygon-side-copy">
                <h2>Gerenciar<br />Polígonos</h2>
                <p>Crie, edite e gerencie os polígonos utilizados para monitoramento de áreas e regras de proximidade.</p>
              </div>

              <div className="polygon-side-features">
                <div className="polygon-side-feature">
                  <span className="polygon-feature-icon blue"><FaPlus /></span>
                  <div>
                    <strong>Cadastro rápido</strong>
                    <small>Adicione novos polígonos de forma prática.</small>
                  </div>
                </div>
                <div className="polygon-side-feature">
                  <span className="polygon-feature-icon green"><FaShieldAlt /></span>
                  <div>
                    <strong>Monitoramento preciso</strong>
                    <small>Defina áreas com precisão para suas operações.</small>
                  </div>
                </div>
                <div className="polygon-side-feature">
                  <span className="polygon-feature-icon violet"><FaListUl /></span>
                  <div>
                    <strong>Organização</strong>
                    <small>Visualize e gerencie todos os polígonos cadastrados.</small>
                  </div>
                </div>
              </div>
            </aside>

            <section className="polygon-manager-content">
              <header className="polygon-manager-header">
                <div className="polygon-manager-title">
                  <span className="polygon-manager-title-icon"><FaDrawPolygon /></span>
                  <div>
                    <h2>Polígonos Cadastrados</h2>
                    <p>Visualize e gerencie todos os polígonos do sistema</p>
                  </div>
                </div>
                <button className="polygon-manager-close" onClick={() => setShowListaPoligonos(false)} aria-label="Fechar">
                  <FaTimes />
                </button>
              </header>

              <button className="polygon-new-button" onClick={handleNovoPoligono}>
                <FaPlus />
                <span>Novo Polígono</span>
              </button>

              <div className="polygon-manager-filters">
                <label className="polygon-search-field">
                  <FaSearch />
                  <input
                    type="text"
                    value={buscaPoligono}
                    onChange={(e) => {
                      setBuscaPoligono(e.target.value);
                      setPaginaPoligonos(1);
                    }}
                    placeholder="Buscar polígonos..."
                  />
                </label>

                <label className="polygon-type-filter">
                  <FaFilter />
                  <select
                    value={filtroTipoPoligono}
                    onChange={(e) => {
                      setFiltroTipoPoligono(e.target.value as 'todos' | Poligono['tipo']);
                      setPaginaPoligonos(1);
                    }}
                  >
                    <option value="todos">Filtrar por tipo</option>
                    <option value="patio">Pátio</option>
                    <option value="cliente">Cliente</option>
                    <option value="posto">Posto</option>
                    <option value="oficina">Oficina</option>
                    <option value="outro">Outro</option>
                  </select>
                </label>
              </div>

              <div className="polygon-card-list">
                {poligonosPaginados.length === 0 ? (
                  <div className="polygon-empty-manager">
                    <FaDrawPolygon />
                    <strong>Nenhum polígono encontrado</strong>
                    <span>Ajuste os filtros ou cadastre um novo polígono.</span>
                  </div>
                ) : (
                  poligonosPaginados.map(pol => (
                    <article className="polygon-list-card" key={pol.id || `${pol.nome}-${pol.coordenadas.length}`}>
                      <span className={`polygon-list-icon polygon-type-${pol.tipo}`}>
                        {getTipoIcon(pol.tipo)}
                      </span>

                      <div className="polygon-list-main">
                        <strong>{pol.nome}</strong>
                        <span className={`polygon-type-badge polygon-type-${pol.tipo}`}>
                          {getTipoLabel(pol.tipo)}
                        </span>
                        {pol.cnpjCliente && <small>CNPJ: {formatarCnpj(pol.cnpjCliente)}</small>}
                      </div>

                      <div className="polygon-list-points">
                        <FaMapMarkerAlt />
                        <span>{pol.coordenadas.length} pontos</span>
                      </div>

                      <div className="polygon-list-actions">
                        <button className="polygon-icon-button edit" onClick={() => handleEditar(pol)} aria-label={`Editar ${pol.nome}`}>
                          <FaEdit />
                        </button>
                        <button
                          className="polygon-icon-button delete"
                          onClick={() => {
                            if (pol.id && window.confirm(`Deseja realmente deletar o polígono "${pol.nome}"?`)) {
                              onDeletar(pol.id);
                            }
                          }}
                          disabled={!pol.id}
                          aria-label={`Excluir ${pol.nome}`}
                        >
                          <FaTrash />
                        </button>
                      </div>
                    </article>
                  ))
                )}
              </div>

              <footer className="polygon-manager-footer">
                <span>Mostrando {primeiroItemPoligonos} a {ultimoItemPoligonos} de {poligonosFiltrados.length} polígonos</span>

                <div className="polygon-pagination">
                  <button onClick={() => mudarPaginaPoligonos(paginaAtualPoligonos - 1)} disabled={paginaAtualPoligonos === 1} aria-label="Página anterior">
                    <FaChevronLeft />
                  </button>
                  {paginasVisiveisPoligonos.map(pagina => (
                    <button
                      key={pagina}
                      className={pagina === paginaAtualPoligonos ? 'active' : ''}
                      onClick={() => mudarPaginaPoligonos(pagina)}
                    >
                      {pagina}
                    </button>
                  ))}
                  <button onClick={() => mudarPaginaPoligonos(paginaAtualPoligonos + 1)} disabled={paginaAtualPoligonos === totalPaginasPoligonos} aria-label="Próxima página">
                    <FaChevronRight />
                  </button>
                </div>

                <select
                  className="polygon-page-size"
                  value={itensPorPaginaPoligonos}
                  onChange={(e) => {
                    setItensPorPaginaPoligonos(Number(e.target.value));
                    setPaginaPoligonos(1);
                  }}
                >
                  <option value={5}>5 por página</option>
                  <option value={10}>10 por página</option>
                  <option value={15}>15 por página</option>
                </select>
              </footer>
            </section>
          </div>
        </div>,
        document.body
      )}

      {false && showListaPoligonos && ReactDOM.createPortal(
        <div style={{position: 'fixed', zIndex: 2147483647, left: 0, top: 0, width: '100vw', height: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
          <div className="modal-overlay" onClick={() => setShowListaPoligonos(false)} style={{position: 'static', zIndex: 'auto'}}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 600 }}>
              <div className="modal-header" style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                borderBottom: '2px solid #e0e0e0',
                background: 'linear-gradient(135deg, #f5f7fa 0%, #e8ebf0 100%)'
              }}>
                <h3 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 700, color: '#1976d2', display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <span style={{ fontSize: '1.75rem' }}>📍</span>
                  <span>Gerenciar Polígonos</span>
                </h3>
                <button className="modal-close" onClick={() => setShowListaPoligonos(false)} style={{
                  fontSize: '1.75rem',
                  padding: '8px 12px',
                  borderRadius: '8px',
                  transition: 'all 0.2s'
                }}>×</button>
              </div>
              <div className="modal-body">
                <button
                  onClick={() => {
                    setModoDesenho(true);
                    setShowListaPoligonos(false);
                  }}
                  className="btn-primary"
                  style={{ 
                    width: '100%', 
                    marginBottom: 20,
                    padding: '12px 24px',
                    fontSize: '0.9375rem',
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px'
                  }}
                >
                  <span style={{ fontSize: '1.125rem' }}>+</span>
                  <span>Novo Polígono</span>
                </button>

                {poligonos.length === 0 ? (
                  <div style={{ 
                    textAlign: 'center', 
                    padding: '60px 20px',
                    background: '#f5f5f5',
                    borderRadius: 12,
                    border: '2px dashed #ccc'
                  }}>
                    <div style={{ fontSize: '3rem', marginBottom: 16 }}>📍</div>
                    <p style={{ color: '#666', fontSize: '1rem', fontWeight: 500, margin: 0 }}>Nenhum polígono cadastrado</p>
                    <p style={{ color: '#999', fontSize: '0.875rem', marginTop: 8 }}>Clique em "Novo Polígono" para começar</p>
                  </div>
                ) : (
                  <div>
                    {poligonos.map(pol => (
                      <div 
                        key={pol.id} 
                        style={{
                          border: '2px solid #e0e0e0',
                          borderRadius: 12,
                          padding: 18,
                          marginBottom: 16,
                          borderLeft: `5px solid ${getTipoCor(pol.tipo)}`,
                          background: '#fafafa',
                          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
                          transition: 'all 0.2s'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = 'translateX(4px)';
                          e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1)';
                          e.currentTarget.style.borderColor = getTipoCor(pol.tipo);
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'translateX(0)';
                          e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.05)';
                          e.currentTarget.style.borderColor = '#e0e0e0';
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: 10 }}>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: 600, fontSize: 16, marginBottom: 6 }}>{pol.nome}</div>
                            <div style={{ fontSize: 13, color: '#666', marginBottom: 4 }}>
                              <span style={{
                                background: getTipoCor(pol.tipo),
                                color: 'white',
                                padding: '3px 10px',
                                borderRadius: 4,
                                fontSize: 12,
                                fontWeight: 500
                              }}>
                                {getTipoLabel(pol.tipo)}
                              </span>
                              <span style={{ marginLeft: 10 }}>{pol.coordenadas.length} pontos</span>
                            </div>
                            {pol.descricao && (
                              <div style={{ fontSize: 13, color: '#666', marginTop: 6 }}>{pol.descricao}</div>
                            )}
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: 8 }}>
                          <button
                            onClick={() => handleEditar(pol)}
                            className="btn-secondary"
                            style={{ 
                              flex: 1,
                              padding: '10px 16px',
                              fontSize: '0.875rem',
                              fontWeight: 600,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '6px'
                            }}
                          >
                            <span>✏️</span>
                            <span>Editar</span>
                          </button>
                          <button
                            onClick={() => {
                              if (window.confirm(`Deseja realmente deletar o polígono "${pol.nome}"?`)) {
                                onDeletar(pol.id!);
                              }
                            }}
                            style={{
                              flex: 1,
                              background: 'linear-gradient(135deg, #f44336 0%, #d32f2f 100%)',
                              color: 'white',
                              border: 'none',
                              padding: '10px 16px',
                              borderRadius: 6,
                              cursor: 'pointer',
                              fontSize: '0.875rem',
                              fontWeight: 600,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '6px',
                              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                              transition: 'all 0.2s'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.transform = 'translateY(-2px)';
                              e.currentTarget.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.15)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.transform = 'translateY(0)';
                              e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.1)';
                            }}
                          >
                            <span>🗑️</span>
                            <span>Deletar</span>
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Modal de configuração do polígono */}
      {showModal && ReactDOM.createPortal(
        <div className="polygon-config-overlay">
          <div className="modal-overlay" onClick={handleCancelar} style={{position: 'static', zIndex: 'auto'}}>
            <div className="modal-content polygon-config-modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header polygon-config-header">
                <div className="polygon-config-heading">
                  <span><FaDrawPolygon /></span>
                  <div>
                    <h3>{poligonoEmEdicao ? 'Editar Polígono' : 'Configurar polígono'}</h3>
                    <p>Preencha os dados para identificar esta área no monitoramento.</p>
                  </div>
                </div>
                <button className="modal-close" onClick={handleCancelar}>×</button>
              </div>
              <div className="modal-body polygon-config-body">
                <div className="form-group polygon-config-name">
                  <label>Nome*</label>
                  <input
                    type="text"
                    value={novoPoligono.nome}
                    onChange={(e) => setNovoPoligono({ ...novoPoligono, nome: e.target.value })}
                    placeholder="Ex: Pátio FKS, Cliente Volks"
                  />
                </div>

                <div className="form-group polygon-config-type">
                  <label>Tipo*</label>
                  <select
                    value={novoPoligono.tipo}
                    onChange={(e) => setNovoPoligono({ ...novoPoligono, tipo: e.target.value as any })}
                  >
                    <option value="patio">Pátio</option>
                    <option value="cliente">Cliente</option>
                    <option value="posto">Posto</option>
                    <option value="oficina">Oficina</option>
                    <option value="outro">Outro</option>
                  </select>
                </div>

                {(novoPoligono.tipo === 'cliente' || novoPoligono.tipo === 'patio') && (
                  <div className="form-group polygon-config-cnpj">
                    <label>CNPJ do cliente</label>
                    <input
                      type="text"
                      inputMode="numeric"
                      value={formatarCnpj(novoPoligono.cnpjCliente || '')}
                      onChange={(e) => setNovoPoligono({ ...novoPoligono, cnpjCliente: e.target.value.replace(/\D/g, '').slice(0, 14) })}
                      placeholder="00.000.000/0000-00"
                      maxLength={18}
                    />
                    <small>Utilizado para relacionar a chegada do veículo a este polígono.</small>
                  </div>
                )}

                <div className="form-group polygon-config-description">
                  <label>Descrição</label>
                  <textarea
                    value={novoPoligono.descricao}
                    onChange={(e) => setNovoPoligono({ ...novoPoligono, descricao: e.target.value })}
                    placeholder="Informações adicionais"
                    style={{ minHeight: 80 }}
                  />
                </div>

                <div className="form-group polygon-config-color">
                  <label>Cor</label>
                  <input
                    type="color"
                    value={novoPoligono.cor}
                    onChange={(e) => setNovoPoligono({ ...novoPoligono, cor: e.target.value })}
                    className="polygon-config-color-input"
                  />
                </div>

                <div className="form-group polygon-config-coordinates">
                  <div className="polygon-config-coordinates-title">
                    <label>Pontos do polígono</label>
                    <strong>{novoPoligono.coordenadas.length} pontos</strong>
                  </div>
                  <div className="polygon-config-coordinate-list">
                    {novoPoligono.coordenadas.map((p, i) => (
                      <div key={i} className="polygon-config-coordinate-item">
                        <span>{i + 1}</span>
                        <div>
                          <small>Latitude</small>
                          <strong>{p.lat.toFixed(6)}</strong>
                        </div>
                        <div>
                          <small>Longitude</small>
                          <strong>{p.lng.toFixed(6)}</strong>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <div className="modal-footer polygon-config-footer">
                <button className="btn-secondary" onClick={handleCancelar}>
                  × Cancelar
                </button>
                <button className="btn-primary" onClick={handleSalvar}>
                  ✓ {poligonoEmEdicao ? 'Atualizar polígono' : 'Salvar polígono'}
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default PoligonoEditor;
